/**
 * Keyboard shortcut parsing, matching and display.
 *
 * Shortcuts are written platform-neutrally with `mod` (⌘ on macOS, Ctrl
 * elsewhere): `mod+shift+z`, `mod+plus`, `escape`. Matching is *exact* on the
 * modifier set, so `mod+z` never fires for `mod+shift+z` — with one exception:
 * `+` is Shift+= on the layouts that define both, so the two keys are matched
 * regardless of Shift, or the standard zoom keystroke would fire nothing.
 */

export interface Shortcut {
  key: string;
  ctrl: boolean;
  meta: boolean;
  shift: boolean;
  alt: boolean;
}

export type Platform = 'mac' | 'other';

const NAMED_KEYS: Record<string, string> = {
  escape: 'escape',
  esc: 'escape',
  enter: 'enter',
  return: 'enter',
  tab: 'tab',
  space: ' ',
  delete: 'delete',
  del: 'delete',
  backspace: 'backspace',
  insert: 'insert',
  home: 'home',
  end: 'end',
  pageup: 'pageup',
  pagedown: 'pagedown',
  up: 'arrowup',
  down: 'arrowdown',
  left: 'arrowleft',
  right: 'arrowright',
  plus: '+',
  minus: '-',
  equal: '=',
  comma: ',',
  period: '.',
  slash: '/',
  backslash: '\\',
  bracketleft: '[',
  bracketright: ']',
  semicolon: ';',
  quote: "'",
  grave: '`',
};

/** Keys whose KeyboardEvent.key is not the lowercase letter form. */
const KEY_ALIASES: Record<string, string> = {
  '+': '+',
  '-': '-',
  arrowup: 'arrowup',
  arrowdown: 'arrowdown',
  arrowleft: 'arrowleft',
  arrowright: 'arrowright',
};

export function currentPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent ?? '';
  return /mac|iphone|ipad|ipod/i.test(ua) ? 'mac' : 'other';
}

function normaliseKey(raw: string): string {
  const lower = raw.trim().toLowerCase();
  if (NAMED_KEYS[lower]) return NAMED_KEYS[lower] as string;
  if (KEY_ALIASES[lower]) return KEY_ALIASES[lower] as string;
  return lower;
}

export function parseShortcut(spec: string, platform: Platform = currentPlatform()): Shortcut {
  const tokens = spec
    .split('+')
    .map((t) => t.trim().toLowerCase())
    .filter((t) => t.length > 0);
  if (tokens.length === 0) throw new Error(`Empty shortcut: "${spec}"`);

  const shortcut: Shortcut = { key: '', ctrl: false, meta: false, shift: false, alt: false };
  const keyTokens: string[] = [];

  for (const token of tokens) {
    switch (token) {
      case 'mod':
      case 'cmd':
      case 'command':
      case 'meta':
        if (token === 'mod') {
          if (platform === 'mac') shortcut.meta = true;
          else shortcut.ctrl = true;
        } else {
          shortcut.meta = true;
        }
        break;
      case 'ctrl':
      case 'control':
        shortcut.ctrl = true;
        break;
      case 'shift':
        shortcut.shift = true;
        break;
      case 'alt':
      case 'option':
        shortcut.alt = true;
        break;
      default:
        keyTokens.push(token);
    }
  }

  if (keyTokens.length === 0) {
    throw new Error(`Shortcut "${spec}" has no key (modifiers alone are not allowed).`);
  }
  shortcut.key = normaliseKey(keyTokens.join('+'));
  return shortcut;
}

export interface KeyEventLike {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  code?: string;
}

export function shortcutFromEvent(event: KeyEventLike): Shortcut {
  return {
    key: normaliseKey(event.key),
    ctrl: event.ctrlKey,
    meta: event.metaKey,
    shift: event.shiftKey,
    alt: event.altKey,
  };
}

export function shortcutsEqual(a: Shortcut, b: Shortcut): boolean {
  return (
    a.key === b.key &&
    a.ctrl === b.ctrl &&
    a.meta === b.meta &&
    a.shift === b.shift &&
    a.alt === b.alt
  );
}

export function matchesShortcut(spec: string, event: KeyEventLike, platform?: Platform): boolean {
  try {
    const parsed = parseShortcut(spec, platform);
    const actual = shortcutFromEvent(event);
    if (shortcutsEqual(parsed, actual)) return true;
    // '+' is Shift+'=' on the keyboards that define both, so an event for one
    // must match a spec for the other, Shift included — otherwise the browser
    // standard Ctrl/Cmd+'+' fires nothing and only '=' works.
    return (
      (parsed.key === '+' || parsed.key === '=') &&
      (actual.key === '+' || actual.key === '=') &&
      parsed.ctrl === actual.ctrl &&
      parsed.meta === actual.meta &&
      parsed.alt === actual.alt
    );
  } catch {
    return false;
  }
}

const MAC_SYMBOLS: Record<string, string> = {
  ctrl: '⌃',
  alt: '⌥',
  shift: '⇧',
  meta: '⌘',
};

const PRETTY_KEYS: Record<string, string> = {
  ' ': 'Space',
  escape: 'Esc',
  enter: 'Enter',
  tab: 'Tab',
  delete: 'Del',
  backspace: '⌫',
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
  '+': '+',
  '-': '−',
};

/** Human label for menus, e.g. `mod+shift+z` → `Ctrl+Shift+Z` (or `⇧⌘Z` on macOS). */
export function formatShortcut(spec: string, platform: Platform = currentPlatform()): string {
  const parsed = parseShortcut(spec, platform);
  const parts: string[] = [];
  if (platform === 'mac') {
    if (parsed.ctrl) parts.push(MAC_SYMBOLS.ctrl as string);
    if (parsed.alt) parts.push(MAC_SYMBOLS.alt as string);
    if (parsed.shift) parts.push(MAC_SYMBOLS.shift as string);
    if (parsed.meta) parts.push(MAC_SYMBOLS.meta as string);
    parts.push(prettyKey(parsed.key));
    return parts.join('');
  }
  if (parsed.ctrl) parts.push('Ctrl');
  if (parsed.alt) parts.push('Alt');
  if (parsed.shift) parts.push('Shift');
  if (parsed.meta) parts.push('Win');
  parts.push(prettyKey(parsed.key));
  return parts.join('+');
}

function prettyKey(key: string): string {
  if (PRETTY_KEYS[key]) return PRETTY_KEYS[key] as string;
  if (key.length === 1) return key.toUpperCase();
  return key.toUpperCase();
}

/** True when the event target owns text input — global shortcuts must stand down. */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  // Check the attribute explicitly: `isContentEditable` is inherited from an
  // ancestor, so the attribute catches the common explicit cases cheaply.
  const attribute = target.getAttribute('contenteditable');
  if (attribute === 'true' || attribute === '') return true;
  return target.isContentEditable === true;
}
